// Smart Daara - Physical school: personal space (teacher: assignments + timetable; student: timetable; parent: children timetables)
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI, userAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { TimetableGrid } from '../../components/physical/TimetableGrid';
import { Printer } from 'lucide-react';

export default function MyPhysicalSpace() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [config, setConfig] = useState(null);
  const [entries, setEntries] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [loading, setLoading] = useState(true);
  const isTeacher = user?.role === 'teacher';
  const isParent = user?.role === 'parent';

  useEffect(() => {
    (async () => {
      try {
        const cfg = await physicalAPI.getTimeslotConfig();
        setConfig(cfg.data);
        if (isParent) {
          const res = await userAPI.getMyChildren();
          const kids = res.data || [];
          setChildren(kids);
          if (kids.length) setSelectedChild(kids[0].user_id || kids[0].id);
        } else {
          const tt = await physicalAPI.myTimetable();
          setEntries(tt.data);
          if (isTeacher) setAssignments((await physicalAPI.myAssignments()).data);
        }
      } catch (e) { /* no data yet */ }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (isParent && selectedChild) {
      physicalAPI.childTimetable(selectedChild).then((r) => setEntries(r.data)).catch(() => setEntries([]));
    }
  }, [selectedChild]);

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="my-physical-space">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <h1 className="text-2xl font-outfit font-bold text-slate-900">
          {isTeacher ? (fr ? 'Mon espace enseignant' : 'My teacher space')
            : isParent ? (fr ? 'Emploi du temps des enfants' : 'Children timetable')
            : (fr ? 'Mon emploi du temps' : 'My timetable')}
        </h1>
        <Button variant="outline" onClick={() => window.print()} data-testid="print-my-tt-btn">
          <Printer className="w-4 h-4 mr-2" />{fr ? 'Imprimer / PDF' : 'Print / PDF'}
        </Button>
      </div>

      {isTeacher && (
        <Card className="border-0 shadow-sm print:hidden">
          <CardHeader><CardTitle className="text-base">{fr ? 'Mes affectations' : 'My assignments'}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {assignments.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                  <Badge className="bg-slate-200 text-slate-700">{a.class_name}</Badge>
                  <span className="font-medium">{a.subject_name}</span>
                  <span className="text-xs text-slate-400">{a.weekly_hours}h/sem · {a.student_count} {fr ? 'élèves' : 'students'}</span>
                  {a.is_main_teacher && <Badge className="bg-amber-100 text-amber-700">{fr ? 'Prof principal' : 'Main teacher'}</Badge>}
                </div>
              ))}
              {assignments.length === 0 && <p className="text-slate-400 text-sm">{fr ? 'Aucune affectation pour le moment' : 'No assignments yet'}</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {isParent && (
        <div className="flex items-center gap-2 print:hidden">
          <span className="text-sm text-slate-600">{fr ? 'Enfant :' : 'Child:'}</span>
          <select className="h-9 border border-slate-200 rounded-md px-2 text-sm" value={selectedChild} onChange={(e) => setSelectedChild(e.target.value)} data-testid="child-select">
            {children.map((c) => (
              <option key={c.user_id || c.id} value={c.user_id || c.id}>{c.first_name} {c.last_name}</option>
            ))}
          </select>
          {children.length === 0 && <span className="text-slate-400 text-sm">{fr ? 'Aucun enfant lié' : 'No linked children'}</span>}
        </div>
      )}

      {config && <TimetableGrid entries={entries} config={config} />}
      {entries.length === 0 && (
        <p className="text-slate-400 text-sm text-center">{fr ? 'Aucun cours planifié pour le moment' : 'No lessons scheduled yet'}</p>
      )}
    </div>
  );
}
